<template>
  <form class="t-pb-5">
    <div v-if="isLoading">
      <loading-alert :loading="isLoading" text="Loading..." />
    </div>

    <div ref="form" v-else>
      <!-- Displaying Location Name and Address -->
      <div class="location-form-field">
        <label>
          Name
        </label>

        <div v-if="form.displayName">
          <p
            v-if="
              location.hasOwnProperty('openingHours') &&
                location.openingHours.hasOwnProperty('data')
            "
          >
            {{ form.name }}
          </p>
          <p v-else class="t-text-red-600">
            Enter Company Name
          </p>

          <div class="t-mt-1 t-text-left">
            <button
              v-if="
                location.ownershipStatus === 'OWNED_BY_YOU' ||
                  location.ownershipStatus === 'OWNED_BY_NO_ONE' ||
                  canEditName
              "
              @click.prevent="onEditNameClick"
              class="t-text-sm t-font-medium t-text-blue-600"
            >
              Edit Name
            </button>
          </div>
        </div>

        <div v-if="form.editingName" class="location-form-field">
          <p
            v-if="errors.has('name')"
            class="t-text-red-700 t-font-medium t-text-sm"
          >
            {{ errors.first("name") }}
          </p>
          <input
            v-model="form.name"
            v-validate="'required'"
            name="name"
            id="name"
            type="text"
          />
        </div>
      </div>

      <!-- Address -->
      <div class="location-form-field">
        <label>
          Address
        </label>

        <p
          v-if="errors.has('addressFields')"
          class="t-text-red-700 t-font-medium t-text-sm"
        >
          {{ errors.first("addressFields") }}
        </p>

        <div v-if="form.displayAddress && !errors.has('addressFields')">
          <p>
            {{
              `${form.address.streetNumber} ${form.address.streetName}, ${
                form.address.suburb
              }, ${form.address.city} ${form.address.postcode}`
            }}
          </p>
          <div class="t-mt-1 t-text-left">
            <button
              v-if="
                location.ownershipStatus === 'OWNED_BY_YOU' ||
                  location.ownershipStatus === 'OWNED_BY_NO_ONE'
              "
              @click="onEditAddressClick"
              class="t-text-sm t-font-medium t-text-blue-600"
            >
              Edit Address
            </button>
          </div>
        </div>
      </div>

      <!-- Start of fieldset where owners can edit a location's address -->
      <fieldset
        v-if="
          location.ownershipStatus === 'OWNED_BY_YOU' ||
            location.ownershipStatus === 'OWNED_BY_NO_ONE'
        "
        name="addressFields"
        v-validate.immediate.continues="'validAddress'"
      >
        <div class="t-flex t-flex-col md:t-flex-row md:t-space-x-4">
          <div class="location-form-field">
            <label for="street-number">
              Street Number
            </label>
            <input
              v-model="form.address.streetNumber"
              name="street-number"
              id="street-number"
              type="text"
            />
          </div>

          <div class="location-form-field">
            <label for="street-name">
              Street Name
            </label>
            <input
              v-model="form.address.streetName"
              name="street-name"
              id="street-name"
              type="text"
            />
          </div>
        </div>
        <div class="t-flex t-flex-col md:t-flex-row md:t-space-x-4">
          <div class="location-form-field">
            <label for="suburb">
              Suburb
            </label>
            <input
              v-model="form.address.suburb"
              name="suburb"
              id="suburb"
              type="text"
            />
          </div>
          <div class="location-form-field">
            <label for="city">
              City
            </label>
            <input
              v-model="form.address.city"
              name="city"
              id="city"
              type="text"
            />
          </div>

          <div class="location-form-field">
            <label for="postcode">
              Postcode
            </label>
            <input
              v-model="form.address.postcode"
              name="postcode"
              id="postcode"
              type="text"
            />
          </div>
        </div>
      </fieldset>
      <!-- End of fieldset where owners can edit a location's address -->

      <!-- Map Marker -->
      <div class="location-form-field">
        <label>
          Marker
        </label>
        <p class="t-text-sm t-text-gray-800">
          Please move the marker to where you think trucks will load and unload
          at this location
        </p>
        <p v-if="errors.has('coords')">
          {{ errors.first("coords") }}
        </p>
        <div id="map" style="width: 100%; height: 300px;"></div>
      </div>
      <!-- End of Map Marker -->

      <section class="t-grid xl:t-grid-cols-2">
        <section class="xl:t-mr-16">
          <div>
            <div class="t-font-medium t-text-2xl t-text-gray-900 t-mb-4">
              Contacts
              <p class="t-text-sm t-text-gray-700 t-mb-0">
                Who to contact at this area.
              </p>
            </div>

            <div class="location-form-field">
              <div class="location-form-field__heading">
                <label for="contact-email">
                  Contact Email
                </label>
                <small>Highly Recommended</small>
              </div>
              <p
                v-if="errors.has('contact-email')"
                class="t-text-red-700 t-font-medium t-text-sm"
              >
                {{ errors.first("contact-email") }}
              </p>
              <input
                v-model="form.contact.email"
                v-validate="'email'"
                id="contact-email"
                name="contact-email"
                type="text"
                value=""
                placeholder="e.g. admin@thislocationswarehouse.co.nz"
              />
              <small>
                Adding a contact email address for this location helps prevent
                delivery delays or higher charges
              </small>
            </div>

            <!-- Customer Code -->
            <div class="location-form-field">
              <div class="location-form-field__heading">
                <label for="customer-code">
                  Customer Code
                </label>
                <small>Optional</small>
              </div>
              <input
                v-model="form.customerCode"
                name="customer-code"
                id="customer-code"
                type="text"
              />
            </div>

            <!-- Contact Details -->
            <div class="t-mb-4">
              <div class="location-form-field">
                <div class="location-form-field__heading">
                  <label for="contact-name">
                    Contact Person
                  </label>
                  <small>Mandatory</small>
                </div>
                <p
                  v-if="errors.has('contact-name')"
                  class="t-text-red-700 t-font-medium t-text-sm"
                >
                  {{ errors.first("contact-name") }}
                </p>
                <input
                  v-model="form.contact.name"
                  v-validate="'required'"
                  name="contact-name"
                  id="contact-name"
                  type="text"
                  value=""
                />
              </div>

              <div class="location-form-field">
                <div class="location-form-field__heading">
                  <label for="contact-phone">
                    Contact Phone
                  </label>
                  <small>Mandatory</small>
                </div>
                <p
                  v-if="errors.has('contact-phone')"
                  class="t-text-red-700 t-font-medium t-text-sm"
                >
                  {{ errors.first("contact-phone") }}
                </p>
                <input
                  v-model="form.contact.phone"
                  v-validate="'required'"
                  name="contact-phone"
                  id="contact-phone"
                  type="text"
                  value=""
                />
              </div>
            </div>
          </div>

          <!-- Notifications -->
          <fieldset class="t-mt-4">
            <div class="t-font-medium t-text-gray-900 t-mb-2 t-text-2xl">
              Notifications
              <p class="t-text-sm t-text-gray-700 t-mb-0">
                Choose which notifications the contact at this area will receive
              </p>
            </div>

            <!-- Email notifications -->
            <div class="t-flex t-flex-row">
              <p class="t-mr-4">Email</p>
              <div class="t-flex t-flex-col">
                <span>
                  <input
                    v-model="form.contact.receiveEmail"
                    id="email-trucks"
                    name="email-trucks"
                    type="checkbox"
                    class="t-mt-1 t-h-4 t-w-4 t-text-indigo-600 t-border-gray-300 t-rounded"
                    value="receiveEmail"
                  />
                  <label
                    for="email-trucks"
                    class="t-font-medium t-text-gray-800 t-mb-0"
                    >Trucks Approaching Area</label
                  >
                </span>

                <span>
                  <input
                    v-model="form.contact.receiveBookingEmail"
                    id="email-bookings"
                    name="email-bookings"
                    type="checkbox"
                    class="t-mt-1 t-h-4 t-w-4 t-text-indigo-600 t-border-gray-300 t-rounded"
                    value="receiveBookingEmail"
                  />
                  <label
                    for="email-bookings"
                    class="t-font-medium t-text-gray-800 t-mb-0"
                  >
                    Booking Updates
                  </label>
                </span>
              </div>
            </div>

            <div class="t-flex t-flex-row">
              <p class="t-mr-4">SMS&nbsp;</p>
              <div class="t-flex t-flex-col">
                <span>
                  <input
                    v-model="form.contact.receiveSms"
                    id="sms-trucks"
                    name="sms-trucks"
                    type="checkbox"
                    class="t-mt-1 t-h-4 t-w-4 t-text-indigo-600 t-border-gray-300 t-rounded"
                    value="receiveSms"
                  />
                  <label
                    for="sms-trucks"
                    class="t-font-medium t-text-gray-800 t-mb-0"
                  >
                    Trucks Approaching Area
                  </label>
                </span>
              </div>
            </div>
          </fieldset>
          <!-- End of Notifications -->
        </section>

        <section>
          <!-- Location Options -->
          <div class="t-font-medium t-text-gray-900 t-mb-0 t-text-2xl">
            Location Features
            <p class="t-text-sm t-text-gray-700 t-mb-0">
              What will you use this location for
            </p>
          </div>

          <div class="t-mt-4">
            <div class="t-flex-col t-mt-4 xl:t-mt-0">
              <p class="t-block t-text-md t-font-bold t-text-gray-800 t-mb-1">
                Location Options *
              </p>
              <p
                v-if="errors.has('location-option')"
                class="t-text-red-600 t-font-medium t-text-sm"
              >
                {{ errors.first("location-option") }}
              </p>

              <div class="location-form-option">
                <input
                  v-model="form.locationOptions"
                  id="pickup"
                  name="location-option"
                  type="checkbox"
                  value="pickup"
                  v-validate.continues="'atLeastOneLocationOption'"
                />
                <div>
                  <label for="pickup">Pickup</label>
                  <p class="t-text-sm t-text-gray-800">
                    Use this location for pickups
                  </p>
                </div>
              </div>

              <div class="location-form-option">
                <input
                  v-model="form.locationOptions"
                  id="delivery"
                  name="location-option"
                  type="checkbox"
                  value="delivery"
                  v-validate.continues="'atLeastOneLocationOption'"
                />
                <div>
                  <label for="delivery">Delivery</label>
                  <p class="t-text-sm t-text-gray-800">
                    Use this location for deliveries
                  </p>
                </div>
              </div>

              <div class="location-form-option">
                <input
                  v-model="form.locationOptions"
                  id="forklift"
                  name="location-option"
                  type="checkbox"
                  value="forklift"
                  v-validate.continues="'atLeastOneLocationOption'"
                />
                <div>
                  <label for="forklift">Forklift</label>
                  <p class="t-text-sm t-text-gray-800">
                    This location has a forklift
                  </p>
                </div>
              </div>

              <div class="location-form-option">
                <input
                  v-model="form.locationOptions"
                  id="depot"
                  name="location-option"
                  type="checkbox"
                  value="depot"
                  v-validate.continues="'atLeastOneLocationOption'"
                />
                <div>
                  <label for="depot">Depot</label>
                  <p class="t-text-sm t-text-gray-800">
                    Use this location as a Freighthub depot
                  </p>
                </div>
              </div>
            </div>
          </div>

          <!-- Special Instructions -->
          <div class="location-form-field">
            <label for="instructions">
              Special Instructions
            </label>
            <textarea
              v-model="form.instructions"
              name="instructions"
              id="instructions"
              rows="3"
              class="t-w-full t-rounded-md t-font-normal t-shadow-sm t-border t-border-gray-400
              t-py-1 t-px-2 focus:t-outline-none focus:t-border-indigo-400"
            />
          </div>
          <!-- End of Location Options and Special Instructions -->

          <!-- Business Hours -->
          <div class="t-h-52 t-mt-0 lg:t-mt-2">
            <fieldset>
              <legend class="t-font-medium t-text-gray-900 t-mb-0">
                Business Hours
              </legend>
              <p class="t-text-sm t-text-gray-700 t-mb-0">
                Let us know when a location is open
              </p>

              <div class="t-space-x-4 t-mb-2 t-mt-2 t-font-medium">
                <button
                  @click.prevent="
                    businessHoursSettings.type = HOURS_TYPE.NORMAL
                  "
                  :class="{
                    't-border-b-2 t-border-blue-500':
                      businessHoursSettings.type === HOURS_TYPE.NORMAL
                  }"
                >
                  Normal
                </button>
                <button
                  @click.prevent="
                    businessHoursSettings.type = HOURS_TYPE.CUSTOM
                  "
                  :class="{
                    't-border-b-2 t-border-blue-500':
                      businessHoursSettings.type === HOURS_TYPE.CUSTOM
                  }"
                >
                  Custom
                </button>
              </div>
              <span
                v-if="errors.has('business-days')"
                class="t-text-red-600 t-font-medium t-text-sm t-mb-2"
              >
                {{ errors.first("business-days") }}
              </span>

              <!-- Normal Business Hours Section -->
              <fieldset
                v-if="businessHoursSettings.type === HOURS_TYPE.NORMAL"
                class="t-grid t-grid-rows-2 t-grid cols-1 t-gap-4 md:t-grid-rows-1 md:t-grid-cols-2 t-my-4"
              >
                <business-days-dropdown
                  :label-text="'From'"
                  :day-index="businessHoursSettings.normal.dayFrom"
                  @businessDayChanged="
                    (hoursType, dayIndex) =>
                      businessDayChangedHandler(hoursType, dayIndex, true)
                  "
                />

                <business-days-dropdown
                  :label-text="'To'"
                  :day-index="businessHoursSettings.normal.dayTo"
                  @businessDayChanged="
                    (hoursType, dayIndex) =>
                      businessDayChangedHandler(hoursType, dayIndex, false)
                  "
                />

                <business-hours-dropdown
                  :label-text="'Opening Time'"
                  :available-business-hours="AVAILABLE_BUSINESS_HOURS"
                  :time-slot="form.businessHours[0].openingHour"
                  @selectedTimeSlotChanged="
                    timeSlot => {
                      businessHourChangedHandler(
                        HOURS_TYPE.NORMAL,
                        timeSlot,
                        true
                      );
                    }
                  "
                />

                <business-hours-dropdown
                  :label-text="'Closing Time'"
                  :available-business-hours="AVAILABLE_BUSINESS_HOURS"
                  :time-slot="form.businessHours[0].closingHour"
                  @selectedTimeSlotChanged="
                    timeSlot => {
                      businessHourChangedHandler(
                        HOURS_TYPE.NORMAL,
                        timeSlot,
                        false
                      );
                    }
                  "
                />
              </fieldset>
              <!-- End of Normal Business Hours Section -->

              <!-- Custom Business Hours Section -->
              <fieldset v-if="businessHoursSettings.type === HOURS_TYPE.CUSTOM">
                <div
                  v-for="(businessHoursObject, dayIndex) in form.businessHours"
                  :key="dayIndex"
                  class="t-grid t-grid-flow-row t-auto-rows-max md:t-grid-rows-1 md:t-grid-cols-3 t-gap-y-2
                  md:t-gap-x-4 t-mt-4 md:t-mt-0 t-border-b-2 t-border-gray-300 t-pb-4 md:t-border-0"
                >
                  <!-- Checkbox if they are open or closed during customised days -->
                  <div class="t-flex t-flex-col">
                    <label
                      class="t-flex t-mb-1 t-font-medium t-text-gray-900"
                      >{{ DAYS_IN_WEEK[dayIndex] }}</label
                    >
                    <input
                      @click="
                        businessDayChangedHandler(HOURS_TYPE.CUSTOM, dayIndex)
                      "
                      type="checkbox"
                      :checked="form.businessHours[dayIndex].open"
                      name="business-days"
                      v-validate.continues="'atLeastOneBusinessDay'"
                      class="t-flex t-w-5 t-h-5"
                    />
                  </div>

                  <business-hours-dropdown
                    :label-text="'Opening Time'"
                    :available-business-hours="AVAILABLE_BUSINESS_HOURS"
                    :time-slot="form.businessHours[dayIndex].openingHour"
                    @selectedTimeSlotChanged="
                      timeSlot => {
                        businessHourChangedHandler(
                          HOURS_TYPE.CUSTOM,
                          timeSlot,
                          true,
                          dayIndex
                        );
                      }
                    "
                  />

                  <business-hours-dropdown
                    :label-text="'Closing Time'"
                    :available-business-hours="AVAILABLE_BUSINESS_HOURS"
                    :time-slot="form.businessHours[dayIndex].closingHour"
                    @selectedTimeSlotChanged="
                      timeSlot => {
                        businessHourChangedHandler(
                          HOURS_TYPE.CUSTOM,
                          timeSlot,
                          false,
                          dayIndex
                        );
                      }
                    "
                  />
                </div>
              </fieldset>
              <!-- End of Custom Business Hours Section -->
            </fieldset>
          </div>
        </section>
      </section>
    </div>

    <!-- Buttons -->
    <div
      :class="'t-justify-between'"
      class="t-flex t-mt-4 t-border-t t-border-gray-700 t-pt-4"
    >
      <button
        v-if="isCreatingLocation"
        @click.prevent="$emit('formBackClicked')"
        class="t-text-lg t-font-medium t-bg-gray-600 t-text-white t-py-2 t-px-4 t-rounded-lg"
      >
        Back
      </button>
      <button
        v-if="!isCreatingLocation"
        @click.prevent="$emit('onDeleteLocationClick', location)"
        class="t-text-lg t-font-medium t-bg-red-600 t-text-white t-py-2 t-px-4 t-rounded-lg"
      >
        Delete
      </button>
      <button
        @click.prevent="onSubmitForm"
        :disabled="isSaving || !form.name"
        class="t-text-lg t-font-medium t-bg-blue-500 t-text-white t-py-2 t-px-4 t-rounded-lg"
      >
        <i v-if="isSaving" class="fa fa-spinner fa-spin" />
        {{ isCreatingLocation ? "Add" : "Save" }}
      </button>
    </div>
  </form>
</template>

<script>
import BusinessDaysDropdown from "../BusinessDaysDropdown";
import BusinessHoursDropdown from "../BusinessHoursDropdown";

export default {
  name: "SaveLocationForm",
  components: {
    BusinessDaysDropdown,
    BusinessHoursDropdown
  },
  emits: ["formBackClicked"],
  $_veeValidate: {
    validator: "new"
  },
  props: {
    location: {
      type: Object,
      required: true
    },
    locationType: {
      type: String,
      default: null
    },
    isCreatingLocation: {
      type: Boolean,
      required: true
    }
  },
  data() {
    return {
      fieldsToFill: null,
      canEditName: false,
      isEditingAddress: false,
      form: {
        id: "",
        name: "",
        displayName: true,
        editingName: false,
        displayAddress: true,
        editingAddress: false,
        address: {
          streetNumber: "",
          streetName: "",
          suburb: "",
          city: "",
          postcode: "",
          DPID: "",
          formattedAddress: "",
          placeId: ""
        },
        customerCode: "",
        coords: {
          lat: null,
          lng: null
        },
        locationOptions: ["delivery"],
        contact: {
          name: "",
          email: "",
          phone: "",
          receiveEmail: false,
          receiveSms: false,
          receiveBookingEmail: false
        },
        instructions: "",
        businessHours: Array.from({ length: 7 }, (val, i) => ({
          day: i + 1,
          open: i <= 4, // Till Friday
          openingHour: "8:30am",
          closingHour: "4:30pm"
        }))
      },
      businessHoursSettings: {
        type: "NORMAL",
        normal: {
          dayFrom: 0,
          dayTo: 4
        }
      },
      isSaving: false,
      isLoading: false
    };
  },
  computed: {
    map() {
      if (this.form.coords.lat && this.form.coords.lng) {
        return {
          zoom: 18,
          center: { lat: this.form.coords.lat, lng: this.form.coords.lng }
        };
      } else {
        return {
          zoom: 7,
          center: { lat: -36.84, lng: 174.76 }
        };
      }
    }
  },
  created() {
    if (this.isCreatingLocation) {
      this.ownershipStatus = this.location.ownershipStatus;
    }

    // Custom Validation
    this.$validator.extend("mapCoordsNotNull", {
      getMessage: () => "Please place a marker at the location",
      validate: () =>
        !(
          this.fieldsToFill.marker &&
          (!this.form.coords || this.form.coords.lat || this.form.coords.lng)
        )
    });

    this.$validator.extend("atLeastOneLocationOption", {
      getMessage: () => "Please select at least one location option",
      validate: () => this.form.locationOptions.length > 0
    });

    this.$validator.extend("atLeastOneBusinessDay", {
      getMessage: () => "Please select at least one operating day",
      validate: () =>
        this.form.businessHours.some(businessDay => businessDay.open)
    });

    this.$validator.extend("businessHoursInOrder", {
      getMessage: () =>
        "Please ensure that opening hours are before closing hours",
      validate: () => {
        for (let i = 0; i < this.form.businessHours.length; i++) {
          let businessHoursObject = this.form.businessHours[i];
          if (businessHoursObject.open) {
            let openingTime = moment(businessHoursObject.openingHour, "h:mma");
            let closingTime = moment(
              businessHoursObject.closingHour,
              "h:mma"
            );

            if (openingTime.isAfter(closingTime)) {
              return false;
            }
          }
        }
        return true;
      }
    });

    this.DAYS_IN_WEEK = [
      "Monday",
      "Tuesday",
      "Wednesday",
      "Thursday",
      "Friday",
      "Saturday",
      "Sunday"
    ];
    this.HOURS_TYPE = { NORMAL: "NORMAL", CUSTOM: "CUSTOM" };
    this.AVAILABLE_BUSINESS_HOURS = (() => {
      let hours = [];
      for (let i = 0; i < 24; i++) {
        hours.push(
          moment()
            .hours(i)
            .minutes(0)
            .format("h:mma")
        );
        hours.push(
          moment()
            .hours(i)
            .minutes(30)
            .format("h:mma")
        );
      }
      return hours;
    })();

    this.processLocationData();

    this.$validator.extend("validAddress", {
      getMessage: () =>
        `We were not able to find all of this location's details. Please fill them out.`,
      validate: () => {
        if (
          !this.form.address.streetNumber ||
          this.form.address.streetNumber === ""
        ) {
          return false;
        } else if (
          !this.form.address.streetName ||
          this.form.address.streetName === ""
        ) {
          return false;
        } else if (
          !this.form.address.suburb ||
          this.form.address.suburb === ""
        ) {
          return false;
        } else if (
          !this.form.address.suburb ||
          this.form.address.suburb === ""
        ) {
          return false;
        } else if (!this.form.address.city || this.form.address.city === "") {
          return false;
        } else if (
          !this.form.address.postcode ||
          this.form.address.postcode === ""
        ) {
          return false;
        }
        return true;
      }
    });

    // Initialize the map
    this.initMap();
  },
  methods: {
    /**
     * Processes values from the given location prop, determines location ownership, and places values into the form.
     */
    processLocationData() {
      if (
        this.location.sourcedFrom &&
        this.location.sourcedFrom === "GOOGLE_PLACES"
      ) {
        this.canEditName = true;
      }

      if (
        this.location.hasOwnProperty("openingHours") &&
        this.location.openingHours.hasOwnProperty("data")
      ) {
        this.form.name = this.location.name;
      } else {
        this.form.name = "";
      }

      this.form.address = {
        streetNumber: this.location.address.data.street_number,
        streetName: this.location.address.data.street_name,
        suburb: this.location.address.data.suburb,
        city: this.location.address.data.city,
        postcode: this.location.address.data.post_code,
        formattedAddress: this.location.address.data.formatted,
        placeId: this.location.address.data.placeId
          ? this.location.address.data.placeId
          : null
      };
      this.form.customerCode = this.location.customer_code;
      this.form.coords = {
        lat: this.location.address.data.latitude,
        lng: this.location.address.data.longitude
      };

      if (!this.isCreatingLocation) {
        this.form.locationOptions = [
          ...(this.location.pickup ? ["pickup"] : []),
          ...(this.location.delivery ? ["delivery"] : []),
          ...(this.location.has_forklift ? ["forklift"] : []),
          ...(this.location.depot ? ["depot"] : [])
        ];
      }

      this.form.contact = {
        name: this.location.contact_name,
        email: this.location.email,
        phone: this.location.phone,
        receiveEmail: this.location.receive_email,
        receiveSms: this.location.receive_sms,
        receiveBookingEmail: this.location.receive_booking_email
      };
      this.form.instructions = this.location.instructions;

      if (this.location.hasOwnProperty("openingHours")) {
        if (this.location.openingHours.hasOwnProperty("data")) {
          if (
            this.location.openingHours.data &&
            this.location.openingHours.data.length > 0
          ) {
            this.businessHoursSettings.type = this.HOURS_TYPE.CUSTOM;
            this.form.businessHours = Array.from(
              { length: this.DAYS_IN_WEEK.length },
              (val, i) => {
                let index = this.location.openingHours.data.findIndex(
                  e => e.weekday_id === i + 1
                );
                if (index !== -1) {
                  return {
                    day: i + 1,
                    open: true,
                    openingHour: moment(
                      this.location.openingHours.data[index].opening_hour,
                      "HH:mm"
                    ).format("h:mma"),
                    closingHour: moment(
                      this.location.openingHours.data[index].closing_hour,
                      "HH:mm"
                    ).format("h:mma")
                  };
                } else {
                  return {
                    day: i + 1,
                    open: false,
                    openingHour: "8:30am",
                    closingHour: "4:30pm"
                  };
                }
              }
            );
          }
        }
      }
    },

    onEditNameClick() {
      this.form.displayName = false;
      this.form.editingName = true;
    },

    onEditAddressClick() {
      this.form.displayAddress = false;
      this.form.editingAddress = true;
    },

    /**
     * Initialize the Google Map and Marker
     */
    initMap() {
      this.$nextTick(() => {
        const mapOptions = {
          zoom: this.map.zoom,
          center: this.map.center,
          mapTypeId: "satellite"
        };
        this.mapInstance = new google.maps.Map(document.getElementById("map"), mapOptions);

        if (this.form.coords.lat && this.form.coords.lng) {
          this.marker = new google.maps.Marker({
            position: { lat: this.form.coords.lat, lng: this.form.coords.lng },
            map: this.mapInstance,
            draggable: true
          });

          this.marker.addListener("dragend", this.updateMarkerPosition);
        }

        this.mapInstance.addListener("click", this.updateMarkerPosition);
      });
    },

    /**
     * Update the marker position
     * @param {Object} event - The event object from the map or marker
     */
    updateMarkerPosition(event) {
      const lat = event.latLng.lat();
      const lng = event.latLng.lng();

      this.form.coords.lat = lat;
      this.form.coords.lng = lng;

      if (this.marker) {
        this.marker.setPosition({ lat, lng });
      } else {
        this.marker = new google.maps.Marker({
          position: { lat, lng },
          map: this.mapInstance,
          draggable: true
        });

        this.marker.addListener("dragend", this.updateMarkerPosition);
      }
    },

    /**
     * Changes what days are open or closed.
     * @param hoursType - If the user selected NORMAL or CUSTOM business hours.
     * @param dayIndex - Position of day within a week.
     * @param isDayFrom - Whether the dayIndex is the start day in the week.
     */
    businessDayChangedHandler(hoursType, dayIndex, isDayFrom = true) {
      if (hoursType === this.HOURS_TYPE.NORMAL) {
        let dayFrom = this.businessHoursSettings.normal.dayFrom;
        let dayTo = this.businessHoursSettings.normal.dayTo;

        if (isDayFrom) {
          dayFrom = dayIndex;
        } else {
          dayTo = dayIndex;
        }

        let weekLength = this.form.businessHours.length;
        if (dayFrom < dayTo) {
          // If operating days are sorted in order e.g. Monday to Friday
          for (let i = 0; i < weekLength; i++) {
            this.form.businessHours[i].open = i >= dayFrom && i <= dayTo;
          }
        } else if (dayFrom > dayTo) {
          // If operating days are in reverse e.g. Friday to Wednesday (open from Fri, Sat, Sun, Mon, Tue, Wed)
          for (let i = dayFrom; i < dayFrom + weekLength; i++) {
            this.form.businessHours[i % weekLength].open =
              i <= weekLength + (dayFrom - dayTo);
          }
        } else {
          this.form.businessHours[dayIndex].open = true;
          dayFrom = dayIndex;
          dayTo = dayIndex;
        }
        this.businessHoursSettings.normal.dayFrom = dayFrom;
        this.businessHoursSettings.normal.dayTo = dayTo;
      } else {
        this.form.businessHours[dayIndex].open = !this.form.businessHours[
          dayIndex
        ].open;
      }
    },

    /**
     *
     * @param hoursType
     * @param timeSlot
     * @param isOpeningHour
     * @param dayIndex
     */
    businessHourChangedHandler(
      hoursType,
      timeSlot,
      isOpeningHour = true,
      dayIndex = 0
    ) {
      let openingTime;
      let closingTime;
      if (isOpeningHour) {
        openingTime = moment(timeSlot, "h:mma");
        closingTime = moment(
          this.form.businessHours[dayIndex].closingHour,
          "h:mma"
        );
      } else {
        openingTime = moment(
          this.form.businessHours[dayIndex].openingHour,
          "h:mma"
        );
        closingTime = moment(timeSlot, "h:mma");
      }

      if (this.businessHoursSettings.type === this.HOURS_TYPE.NORMAL) {
        for (let i = 0; i < this.form.businessHours.length; i++) {
          if (isOpeningHour) {
            this.form.businessHours[i].openingHour = timeSlot;
          } else {
            this.form.businessHours[i].closingHour = timeSlot;
          }
        }
      } else {
        if (isOpeningHour) {
          this.form.businessHours[dayIndex].openingHour = timeSlot;
        } else {
          this.form.businessHours[dayIndex].closingHour = timeSlot;
        }
      }
    },

    onSubmitForm() {
      this.isSaving = true;

      // Validate fields
      const onSubmitFormHandler = async () => {
        let isValid = await this.$validator.validateAll();

        if (isValid) {
          try {
            let config = {
              headers: {
                "Content-Type": "application/json"
              }
            };

            let data = {
              is_owner: this.ownershipStatus === "OWNED_BY_YOU" ? 1 : 0,
              name: this.form.name,
              street_number: this.form.address.streetNumber,
              street_name: this.form.address.streetName,
              suburb: this.form.address.suburb,
              city: this.form.address.city,
              post_code: this.form.address.postcode,
              DPID: this.form.address.DPID,
              google_place_id: this.form.address.placeId,
              formatted: this.form.address.formattedAddress,
              lat: this.form.coords.lat,
              lng: this.form.coords.lng,
              customer_code: this.form.customerCode,
              contact_name: this.form.contact.name,
              email: this.form.contact.email,
              phone: this.form.contact.phone,
              receive_email: this.form.contact.receiveEmail,
              receive_sms: this.form.contact.receiveSms,
              receive_booking_email: this.form.contact.receiveBookingEmail,
              pickup: !!this.form.locationOptions.includes("pickup"),
              delivery: !!this.form.locationOptions.includes("delivery"),
              has_forklift: !!this.form.locationOptions.includes("forklift"),
              depot: !!this.form.locationOptions.includes("depot"),
              instructions: this.form.instructions,
              opening_hours: this.form.businessHours.reduce(
                (dayOpenings, dayInfo) => ({
                  ...dayOpenings,
                  [dayInfo.day]: {
                    open: dayInfo.open,
                    opening_hour: moment(dayInfo.openingHour, "h:mma").format(
                      "HH:mm"
                    ),
                    closing_hour: moment(dayInfo.closingHour, "h:mma").format(
                      "HH:mm"
                    )
                  }
                }),
                {}
              )
            };

            if (this.isCreatingLocation) {
              // Endpoint that creates a new location
              await axios.post("/settings/locations", data, config);

              this.$parent.$emit("submitted");
            } else {
              await axios.patch(
                `/settings/locations/${this.location.id}`,
                data,
                config
              );

              let payload = {
                id: this.location.id,
                type: this.locationType
              };
              this.$parent.$emit("submitted", payload);
            }
            this.isSaving = false;

            await this.$swal({
              title: "Location Saved!",
              type: "success",
              confirmButtonText: "Okay"
            });
            this.$emit("locationChanged");
          } catch (error) {
            this.isSaving = false;
          }
        } else {
          this.$refs.form.scrollIntoView();
        }
        this.isSaving = false;
      };
      onSubmitFormHandler();
    }
  }
};
</script>

<style scoped></style>
